/**
 * The live productions, cross-tenant (D27): where each runs, whether it is
 * on air, its main/backup protection (unprotected is red: PLAN §8.2's "the
 * production runs unprotected, and the UI shows a warning"), its devices and
 * whether the cloud's view of it is stale.
 */
import type { VendorProductionWire } from '@acp/api-contract/vendor.js';
import { protectionTone, shortHash } from '../../lib/vendor.js';
import { Badge } from '../ui/badge.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type LiveProductionsTableProps = {
  readonly productions: readonly VendorProductionWire[];
  readonly showTenant: boolean;
};

export function LiveProductionsTable({ productions, showTenant }: LiveProductionsTableProps) {
  if (productions.length === 0) return <p className="py-4 text-sm text-muted-foreground" data-testid="productions-none">No live productions.</p>;
  return (
    <Table data-testid="live-productions-table">
      <TableHeader>
        <TableRow>
          {showTenant ? <TableHead>Tenant</TableHead> : null}
          <TableHead>Production</TableHead>
          <TableHead>Studio</TableHead>
          <TableHead>Site</TableHead>
          <TableHead>On air</TableHead>
          <TableHead>Protection</TableHead>
          <TableHead>Devices</TableHead>
          <TableHead>Package</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {productions.map((p) => (
          <TableRow key={`${p.tenantId}/${p.productionId}`} data-testid="production-row" data-production={p.productionId}>
            {showTenant ? <TableCell className="font-mono text-xs">{p.tenantId}</TableCell> : null}
            <TableCell className="font-mono text-xs">{p.productionId}</TableCell>
            <TableCell className="text-sm">{p.studioId}</TableCell>
            <TableCell className="text-sm">{p.siteId ?? '–'}</TableCell>
            <TableCell>{p.onAir ? <Badge variant="onair">on air</Badge> : <span className="text-sm text-muted-foreground">no</span>}</TableCell>
            <TableCell>
              <div className="flex items-center gap-1">
              <Badge variant={protectionTone(p.protection)} data-testid="production-protection" data-protection={p.protection}>
                {p.protection}
              </Badge>
              {p.stale ? (
                <Badge variant="offline" title="The cloud has not heard this production’s node within the stale window" data-testid="production-stale">
                  stale
                </Badge>
              ) : null}
              </div>
            </TableCell>
            <TableCell className="text-sm">
              {p.devices.disconnected > 0 ? (
                <Badge variant="warning" data-testid="production-devices">
                  {p.devices.disconnected} of {p.devices.total} disconnected
                </Badge>
              ) : (
                <span data-testid="production-devices">{p.devices.total} connected</span>
              )}
            </TableCell>
            <TableCell className="font-mono text-xs">{shortHash(p.packageHash)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
